import React from 'react';
import Select from '../ui/Select';
import Input from '../ui/Input';
import TokenChips from './TokenChips';
import ShortLinkField from './ShortLinkField';
import RcsPreview from '../campaigns/RcsPreview';
import { campaignService } from '../../services/campaignService';
import styles from './MessagingFields.module.css';

const FETCH_DEBOUNCE_MS = 500;

const LIST_PLACEHOLDERS = {
  loading: 'Loading templates…',
  error: "Couldn't load templates — check RCS is configured",
  loaded_empty: 'No approved templates found',
};

// RCS (MSG91) requires a pre-approved template like WhatsApp does — positional
// params, not free text.
class RcsFields extends React.Component {
  constructor(props) {
    super(props);
    this.state = { fetchedTemplate: null, fetchStatus: 'idle', templateList: [], templateListStatus: 'loading' };
    this._debounceTimer = null;
    // Guards against a slow earlier request resolving after a faster later
    // one and clobbering it with stale data.
    this._fetchSeq = 0;
  }

  componentDidMount() {
    this._scheduleFetch(this.props.templateId);
    this._loadTemplateList();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.templateId !== this.props.templateId) {
      this._scheduleFetch(this.props.templateId);
    }
  }

  componentWillUnmount() {
    clearTimeout(this._debounceTimer);
  }

  _loadTemplateList() {
    campaignService.listRcsTemplates()
      .then((result) => {
        this.setState({ templateList: result.templates, templateListStatus: result.reason ? 'error' : 'loaded' });
      })
      .catch(() => this.setState({ templateListStatus: 'error' }));
  }

  _scheduleFetch(templateId) {
    clearTimeout(this._debounceTimer);
    if (!templateId || !templateId.trim()) {
      this._fetchSeq++;
      this.setState({ fetchedTemplate: null, fetchStatus: 'idle' });
      return;
    }
    this.setState({ fetchStatus: 'loading' });
    this._debounceTimer = setTimeout(() => this._fetch(templateId), FETCH_DEBOUNCE_MS);
  }

  _fetch(templateId) {
    const seq = ++this._fetchSeq;
    campaignService.getRcsTemplate({ templateId })
      .then((result) => {
        if (seq !== this._fetchSeq) return;
        if (result.found) this.setState({ fetchedTemplate: { functionName: result.functionName, content: result.content }, fetchStatus: 'found' });
        else this.setState({ fetchedTemplate: null, fetchStatus: result.reason === 'not_configured' ? 'error' : 'not_found' });
      })
      .catch(() => {
        if (seq !== this._fetchSeq) return;
        this.setState({ fetchedTemplate: null, fetchStatus: 'error' });
      });
  }

  render() {
    const { templateId = '', templateParams = '', attributionLinkId = '', onChange, showPreview = true } = this.props;
    const { fetchedTemplate, fetchStatus, templateList, templateListStatus } = this.state;

    const options = templateList.map((t) => ({
      value: t.id,
      label: t.title ? `${t.id} — ${t.title}` : t.id,
    }));
    const placeholder = templateListStatus === 'loaded' && !options.length
      ? LIST_PLACEHOLDERS.loaded_empty
      : LIST_PLACEHOLDERS[templateListStatus] || 'Select a template';

    return (
      <div className={showPreview ? styles.msgLayout : undefined}>
        <div className={styles.msgFields}>
          <div className={styles.fi}>
            <label className={styles.fiLabel}>Template ID <span className={styles.req}>*</span></label>
            <Select
              placeholder={placeholder}
              value={templateId}
              options={options}
              onChange={val => onChange('templateId', val)}
            />
            <div className={styles.fiHint}>Approved templates from your MSG91 RCS account.</div>
          </div>
          <div className={styles.fi}>
            <label className={styles.fiLabel}>Template params <span className={styles.opt}>(comma-separated, in order)</span></label>
            <Input placeholder="e.g. Rahul, 10%" value={templateParams} onChange={e => onChange('templateParams', e.target.value)} />
            <TokenChips onInsert={token => onChange('templateParams', templateParams ? `${templateParams}, ${token}` : token)} />
            <ShortLinkField
              attributionLinkId={attributionLinkId}
              onTag={link => onChange('attributionLinkId', link.id)}
              onUntag={() => onChange('attributionLinkId', '')}
            />
          </div>
        </div>
        {showPreview && (
          <RcsPreview
            templateId={templateId}
            templateParams={templateParams}
            fetchedTemplate={fetchedTemplate}
            fetchStatus={fetchStatus}
          />
        )}
      </div>
    );
  }
}

export default RcsFields;
